// Timed events for the practice sounds (F3.7 metronome, F3.8 chord synth), built from an analysis.
import { beatInBar } from '../analysis/grid';
import type { BeatGrid, ChordSegment } from '../analysis/types';
import type { Chord } from '../theory/chord';
import { chordTones } from '../theory/chord';
import { mod } from '../util/math';

export interface ClickEvent {
  time: number;
  /** Beat 1 of a bar. */
  accent: boolean;
}

export interface ChordEvent {
  time: number;
  /** MIDI notes to play, bass first. */
  notes: number[];
}

/** One click per beat, accented on beat 1. */
export function metronomeEvents(grid: BeatGrid): ClickEvent[] {
  return grid.beats.map((time, i) => ({ time, accent: beatInBar(grid, i) === 1 }));
}

/**
 * MIDI notes for a chord: the root in the bass (E2–D♯3) and the chord tones above it
 * (G3 up), shifted by `semitones` to match pitch-shifted audio.
 */
export function chordVoicing(chord: Chord, semitones = 0): number[] {
  const tones = chordTones(chord).map((pc) => mod(pc + semitones, 12));
  const bass = 40 + mod(tones[0]! - 4, 12);
  const upper = tones.map((pc) => 55 + mod(pc - 7, 12)).sort((a, b) => a - b);
  return [bass, ...upper];
}

/** The synth strikes each chord when it starts and again on every downbeat while it lasts. */
export function synthEvents(segments: ChordSegment[], grid: BeatGrid, semitones = 0): ChordEvent[] {
  const events: ChordEvent[] = [];
  for (const seg of segments) {
    if (!seg.chord) continue;
    const notes = chordVoicing(seg.chord, semitones);
    for (let b = seg.startBeat; b < seg.startBeat + seg.beatCount; b++) {
      const time = grid.beats[b];
      if (time === undefined) break;
      if (b === seg.startBeat || beatInBar(grid, b) === 1) events.push({ time, notes });
    }
  }
  return events;
}

/**
 * Pitch shift that makes the recording match a transpose (F3.3): exactly the transpose, so the
 * recording moves the way the player asked (+7 sounds higher, not 5 lower). Large shifts sound
 * less natural; the same key is often a smaller step the other way (+7 or −5).
 */
export function audioShiftFor(transpose: number): number {
  return transpose;
}
